"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

interface ThemeContextType {
  isDarkMode: boolean;
  darkModeProgress: number; // 0 to 1, for the dark overlay
  navbarDarkMode: boolean; // separate control for navbar
  isSandstoneMode: boolean;
  sandstoneProgress: number; // 0 to 1, for the sandstone overlay
}

const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  darkModeProgress: 0,
  navbarDarkMode: false,
  isSandstoneMode: false,
  sandstoneProgress: 0,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkModeProgress, setDarkModeProgress] = useState(0);
  const [navbarDarkMode, setNavbarDarkMode] = useState(false);
  const [isSandstoneMode, setIsSandstoneMode] = useState(false);
  const [sandstoneProgress, setSandstoneProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const feature2Element = document.querySelector('[data-section="feature2"]');
      const feature3Element = document.querySelector('[data-section="feature3"]');
      const landingFeature3Element = document.querySelector('[data-section="landing-feature3"]');
      const caseStudiesElement = document.querySelector('[data-section="case-studies"]');
      const blogElement = document.querySelector('[data-section="blog"]');
      const competitorElement = document.querySelector('[data-section="competitor"]');

      let progress = 0;
      let navbarShouldBeDark = false;
      let isInDarkSection = false;
      let sandstoneProgress = 0;
      let isInSandstoneSection = false;

      // Check if we're on the case studies, blog, or competitor pages - if so, force sandstone mode
      if (caseStudiesElement || blogElement || competitorElement) {
        sandstoneProgress = 1;
        isInSandstoneSection = true;
      }

      // Check Feature2 for dark mode transition (starts at 99.99% through)
      if (feature2Element) {
        const rect2 = feature2Element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const sectionHeight = rect2.height;

        // Calculate Feature2 scroll progress
        let feature2Progress = 0;
        if (rect2.top <= windowHeight && rect2.bottom >= 0) {
          const scrolledDistance = windowHeight - rect2.top;
          const totalScrollDistance = sectionHeight + windowHeight;
          const paddedStart = totalScrollDistance * 0.1;
          const paddedEnd = totalScrollDistance * 0.8;

          if (scrolledDistance >= paddedStart) {
            feature2Progress = Math.min(1, (scrolledDistance - paddedStart) / (paddedEnd - paddedStart));
          }
        } else if (rect2.bottom < 0) {
          feature2Progress = 1;
        }

        // Feature2 dark mode starts at 99.99% scroll progress
        const darkModeStart = 0.9999;
        if (feature2Progress >= darkModeStart && rect2.bottom > 0) {
          // Only apply dark mode if Feature2 is still visible
          const darkProgress = (feature2Progress - darkModeStart) / (1 - darkModeStart);
          progress = darkProgress;
          navbarShouldBeDark = true;
          isInDarkSection = true;
        }
      }

      // Check Feature3 for continued dark mode
      if (feature3Element) {
        const rect = feature3Element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Start dark transition as soon as Feature 3 comes into view
        const startTransition = windowHeight;
        const endTransition = windowHeight * 0.5;

        // Keep dark mode until Feature 3 is almost completely out of view
        const startLightTransition = -rect.height + windowHeight * 0.8;
        const endLightTransition = -rect.height + windowHeight * 0.7;

        // Navbar stays dark longer than the overlay
        const navbarLightTransition = -rect.height + windowHeight * 0.1;

        if (rect.top <= startTransition && rect.top >= endTransition) {
          // We're in the transition zone entering dark mode
          const feature3Progress = (startTransition - rect.top) / (startTransition - endTransition);
          progress = Math.max(progress, feature3Progress);
          navbarShouldBeDark = true;
          isInDarkSection = true;
        } else if (rect.top < endTransition && rect.top > startLightTransition) {
          // We're fully in the dark section - stay dark throughout Feature 3
          progress = 1;
          navbarShouldBeDark = true;
          isInDarkSection = true;
        } else if (rect.top <= startLightTransition && rect.top >= endLightTransition) {
          // We're transitioning back to light mode as feature 3 scrolls out
          const lightProgress = (rect.top - endLightTransition) / (startLightTransition - endLightTransition);
          progress = lightProgress;
          navbarShouldBeDark = rect.top < navbarLightTransition;
          isInDarkSection = true;
        } else if (rect.top < endLightTransition) {
          // Feature 3 is completely out of view - back to light mode
          progress = 0;
          navbarShouldBeDark = false;
          isInDarkSection = false;
        }

        // Override navbar control for Feature3 - stays dark longer
        if (rect.top < navbarLightTransition) {
          navbarShouldBeDark = true;
        }
      }

      // Check landing Feature3 for sandstone mode transition
      if (landingFeature3Element) {
        const rect = landingFeature3Element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Start sandstone transition when Feature3 comes into view
        const startTransition = windowHeight * 0.8;
        const endTransition = windowHeight * 0.2;

        // Keep sandstone mode throughout Feature3
        const startLightTransition = -rect.height + windowHeight * 0.8;
        const endLightTransition = -rect.height + windowHeight * 0.6;

        if (rect.top <= startTransition && rect.top >= endTransition) {
          // We're in the transition zone entering sandstone mode
          const feature3Progress = (startTransition - rect.top) / (startTransition - endTransition);
          sandstoneProgress = feature3Progress;
          isInSandstoneSection = true;
        } else if (rect.top < endTransition && rect.top > startLightTransition) {
          // We're fully in the sandstone section - stay sandstone throughout Feature3
          sandstoneProgress = 1;
          isInSandstoneSection = true;
        } else if (rect.top <= startLightTransition && rect.top >= endLightTransition) {
          // We're transitioning back to light mode as feature 3 scrolls out
          const lightProgress = (rect.top - endLightTransition) / (startLightTransition - endLightTransition);
          sandstoneProgress = lightProgress;
          isInSandstoneSection = true;
        } else if (rect.top < endLightTransition) {
          // Feature 3 is completely out of view - back to light mode
          sandstoneProgress = 0;
          isInSandstoneSection = false;
        }
      }

      // If we're not in any dark section, ensure progress is 0
      if (!isInDarkSection) {
        progress = 0;
      }

      // If we're not in any sandstone section, ensure sandstone progress is 0
      if (!isInSandstoneSection) {
        sandstoneProgress = 0;
      }

      progress = Math.min(1, Math.max(0, progress));
      sandstoneProgress = Math.min(1, Math.max(0, sandstoneProgress));

      setDarkModeProgress(progress);
      setIsDarkMode(progress > 0.05);
      setNavbarDarkMode(navbarShouldBeDark);
      setSandstoneProgress(sandstoneProgress);
      setIsSandstoneMode(sandstoneProgress > 0.05);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <ThemeContext.Provider value={{ isDarkMode, darkModeProgress, navbarDarkMode, isSandstoneMode, sandstoneProgress }}>
      {children}
    </ThemeContext.Provider>
  );
};
